import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface MasterUnitFilters {
  status_id?: number | '';
  journey_type?: 'Forward' | 'Reverse' | 'Failed Delivery' | '';
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface City {
  id: number;
  name: string;
  code?: string;
}

export interface Branch {
  id: number;
  name: string;
  city_id?: number;
}

export interface User {
  id: number;
  name: string;
  type?: string;
}

export interface Status {
  id: number;
  name: string;
  badge?: string;
}

export interface Consignment {
  id: number;
  consignment_no: string;
  booking_type: string;
  service_type: string;
  created_at: string;
  chargeable_weight: number;
  status: Status;
  statusInMasterUnit?: string;
  reasonInMasterUnit?: string;
  attachmentInMasterUnit?: string;
  cod_amount?: number;
  total_price?: number;
}

export interface MasterUnitShipment {
  id: number;
  vehicle_no: string;
  contact_no: string;
  company_name: string;
  eta: string;
  attachment?: string;
  created_at: string;
}

export interface MasterUnit {
  id: number;
  mu_number: string;
  seal_number: string;
  mu_type: string;
  type: string;
  journey_type: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  branch_id: number;
  client_id?: number;
  rider_id?: number;
  inbound_branch_id: number;
  origin: number;
  destination?: number;
  status_id: number;
  user?: User;
  branch?: Branch;
  inboundBranch?: Branch;
  client?: User;
  status?: Status;
  originCity?: City;
  destinationCity?: City;
  consignments?: Consignment[];
  shipment?: MasterUnitShipment;
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  per_page: number;
  total: number;
  last_page: number;
}

export interface CreateMasterUnitRequest {
  seal_number: string;
  mu_type: 'Sag Bag' | 'Mother Bag';
  type: 'Bulky' | 'Non Bulky' | 'COD' | 'Express';
  journey_type: 'Forward' | 'Reverse' | 'Failed Delivery';
  origin: number;
  destination?: number;
  inbound_branch_id?: number;
  client_id?: number;
  consignmentIds: number[];
}

export interface UpdateMasterUnitRequest {
  seal_number: string;
  mu_type: 'Sag Bag' | 'Mother Bag';
  type: 'Bulky' | 'Non Bulky' | 'COD' | 'Express';
  destination: number;
  inbound_branch_id: number;
  consignmentIds: number[];
}

export interface ShipMasterUnitRequest {
  vehicle_no: string;
  contact_no: string;
  company_name: string;
  eta: string;
  attachment: File;
}

export interface ScanConsignmentRequest {
  consignment_no: string;
  destination_id: number;
  journey_type: 'Forward' | 'Reverse' | 'Failed Delivery';
  client_id?: number;
}

export interface ScanConsignmentResponse {
  id: number;
  consignment_no: string;
  booking_type: string;
  service_type: string;
  created_at: string;
  chargeable_weight: number;
  status: string;
}

// Master Unit API functions
export async function getMasterUnits(filters: MasterUnitFilters = {}) {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });

    const response = await apiClient.get(`/master-units?${params.toString()}`);
    const responseData = response.data;

    // Unwrap common { success, data } envelope if present
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;

    return payload as PaginatedResponse<MasterUnit>;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to fetch master units');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function getMasterUnit(id: number) {
  try {
    const response = await apiClient.get(`/master-units/${id}`);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as MasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to fetch master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function createMasterUnit(data: CreateMasterUnitRequest) {
  try {
    const response = await apiClient.post('/master-units', data);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as MasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to create master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function updateMasterUnit(id: number, data: UpdateMasterUnitRequest) {
  try {
    const response = await apiClient.put(`/master-units/${id}`, data);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as MasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to update master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function deleteMasterUnit(id: number) {
  try {
    const response = await apiClient.delete(`/master-units/${id}`);
    const responseData = response.data;
    return responseData;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to delete master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function shipMasterUnit(id: number, data: ShipMasterUnitRequest) {
  try {
    const formData = new FormData();
    formData.append('vehicle_no', data.vehicle_no);
    formData.append('contact_no', data.contact_no);
    formData.append('company_name', data.company_name);
    formData.append('eta', data.eta);
    formData.append('attachment', data.attachment);

    const response = await apiClient.post(`/master-units/${id}/ship`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as MasterUnit;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to ship master unit');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function getCityBranches(cityId: number) {
  try {
    const response = await apiClient.get(`/master-units/cities/${cityId}/branches`);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as Branch[];
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to fetch branches');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function scanConsignment(data: ScanConsignmentRequest) {
  try {
    const response = await apiClient.post('/master-units/consignments/scan', data);
    const responseData = response.data;
    const payload = (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData)
      ? (responseData as any).data
      : responseData;
    return payload as ScanConsignmentResponse;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error((error.response.data as any)?.message || 'Failed to scan consignment');
    }
    throw new Error('An unknown error occurred');
  }
}

export async function printMasterUnit(id: number, awb: boolean = false) {
  const publicUrl = `${API_BASE_URL}/public/master-units/${id}/print?awb=${awb ? '1' : '0'}`;
  window.open(publicUrl, '_blank', 'noopener,noreferrer');
}
